import Vue from 'vue'
import Vuex from 'vuex'
import { Auth } from '@aws-amplify/auth'
import createPersistedState from 'vuex-persistedstate'
import { API } from '@/plugins/api'
import { CompanyCatCd, InspCat, AuthProcessCat } from '@/enum'

Vue.use(Vuex)

function defaultState() {
  return {
    isEMSOnly: false,
    loading: false,
    redirectUrl: '',
    message: '',
    messageType: '',
    cognito: null,
    userAttr: null,
    functionCd: '',
    functionTitle: 'SPARQ',
  }
}

const store = new Vuex.Store({
  state: defaultState(),
  getters: {
    isEMSOnly(state) { return state.isEMSOnly },
    loading(state) { return state.loading },
    redirectUrl(state) { return state.redirectUrl },
    message(state) { return state.message },
    messageType(state) { return state.messageType },
    functionCd(state) { return state.functionCd },
    functionTitle(state) { return state.functionTitle },
    isLoggedIn(state) { return state.cognito !== null },
    cognito(state) { return state.cognito },
    userCd(state) { return state.userAttr !== null ? state.userAttr.userCd : '' },
    userName(state) { return state.userAttr !== null ? state.userAttr.userName : '' },
    companyCd(state) { return state.userAttr !== null ? state.userAttr.company.companyCd : '' }, // ログインユーザー所属会社
    companyCatCd(state) { return state.userAttr !== null ? state.userAttr.company.companyCatCd : '' }, // ログインユーザー所属会社の会社分類
    companyInspCat(state) { return state.userAttr !== null ? state.userAttr.company.inspCat : '' }, // ログインユーザー所属会社の検査実施区分
    userRoles(state) { return state.userAttr !== null ? state.userAttr.roles : [] }, // ログインユーザーのロール一覧
    allowedFunctions(state) { return state.userAttr !== null ? state.userAttr.functions : [] }, // ログインユーザーの使用可能な機能一覧
    isEpson(state) { // ログインユーザーがEPSON所属か判定
      if (state.userAttr == null) {
        return false
      }
      let companyCatCd = state.userAttr.company.companyCatCd
      return companyCatCd == CompanyCatCd.EPSON
    },
    isSubassyVendor(state) {// ログインユーザーがサブ組ベンダーか判定
      if (state.userAttr == null) {
        return false
      }
      let companyCatCd = state.userAttr.company.companyCatCd
      let inspCat = state.userAttr.company.inspCat
      return (companyCatCd == CompanyCatCd.SUPPLIER && inspCat == InspCat.OQC_IQC)
    },
    isSupplier(state) { // ログインユーザーがサプライヤー所属か判定(サブ組ベンダーも含む)
      if (state.userAttr == null) {
        return false
      }
      let companyCatCd = state.userAttr.company.companyCatCd
      return companyCatCd == CompanyCatCd.SUPPLIER
    },
    canOQCOnly(state) { // ログインユーザーがOQCのみ実施可能か判定
      if (state.userAttr == null) {
        return false
      }
      let inspCat = state.userAttr.company.inspCat
      return inspCat == InspCat.OQC
    },
    canIQC(state) { // ログインユーザーがIQC/OQC実施可能か判定
      if (state.userAttr == null) {
        return false
      }
      let inspCat = state.userAttr.company.inspCat
      return inspCat == InspCat.OQC_IQC
    },
    getIQCorOQC(state) {
      if (state.userAttr == null) {
        return ''
      }
      return state.userAttr.company.inspCat == InspCat.OQC ? InspCat.OQC : InspCat.OQC_IQC
    },
    canMasterMaintenance(state) { // ログインユーザーがマスタメンテナンス実施可能か判定
      if (state.userAttr == null) {
        return false
      }
      return state.userAttr.canMasterMaintenance
    },
    hasRole: (state) => (roleCd) => {
      if (state.userAttr == null) return false
      return state.userAttr.roles.some((role) => role == roleCd)
    },
    hasEditRole(state) { // 実行中機能で編集権限を持っているか判定
      if (state.userAttr == null) return false
      return state.userAttr.functions.some(
        (v) => v.functionCd == state.functionCd && v.processCat == AuthProcessCat.EDIT,
      )
    },
  },
  mutations: {
    setLoginType(state, isEMSOnly) {
      state.isEMSOnly = isEMSOnly
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setRedirectUrl(state, redirectUrl) {
      state.redirectUrl = redirectUrl
    },
    setMessageInfo(state, messageinfo) {
      state.messageType = messageinfo[0]
      if (messageinfo[1] == null) {
        state.message = ''
      } else {
        state.message = messageinfo[1]
      }
    },
    addMessageInfo(state, messageinfo) {
      state.messageType = messageinfo[0]
      if (state.message == '') {
        state.message = messageinfo[1]
      } else {
        state.message = state.message + '\n' + messageinfo[1]
      }
    },
    setUser(state, user) {
      if (user) {
        state.cognito = {
          username: user.username ? user.username : '',
          email: user.email ? user.email : ''
        }
      } else {
        state.cognito = null
      }
    },
    setFunction(state, { functionCd, functionTitle }) {
      state.functionCd = functionCd
      state.functionTitle = functionTitle
    },
    setUserAttr(state, attr) {
      state.userAttr = attr
    },
    reset(state) {
      Object.assign(state, defaultState())
    }
  },
  actions: {
    doSetLoginType({ commit }, payload) {
      commit('setLoginType', payload.isEMSOnly)
    },
    doSetLoading({ commit }, payload) {
      commit('setLoading', payload.loading)
    },
    doSetRedirectUrl({ commit }, payload) {
      commit('setRedirectUrl', payload.redirectUrl)
    },
    doSetMessageInfo({ commit }, payload) {
      commit('setMessageInfo', [payload.type, payload.message])
    },
    doAddMessageInfo({ commit }, payload) {
      commit('addMessageInfo', [payload.type, payload.message])
    },
    async doSignIn({ commit }, payload) {
      /*
      cognito サインイン およびユーザーマスターチェック
      cognito認証トークンを習得するためCognitoサインイン処理はフロントで実施する

      https://docs.aws.amazon.com/ja_jp/cognito/latest/developerguide/amazon-cognito-user-pools-authentication-flow.html
      [ID／パスワードの組み合わせが不正の時]
       code: "NotAuthorizedException"
       message: "Incorrect username or password."
       name: "NotAuthorizedException"

      [複数回の認証エラーが発生した時]
       code: "NotAuthorizedException"
       message: "Password attempts exceeded"
       name: "NotAuthorizedException"

      [挙動]
      サインインの試行の失敗は 5 回まで許容
      ロック後の再試行の失敗許容回数は１回許容
      規定の失敗回数に達すると、一時的なロックアウトが開始する
      ロック時間は最初は1秒。再試行後に失敗すると、時間は倍増し15分まで到達する
      15分間サインインを再試行せずに待つと、ロックアウトがリセットされる
      パスワードリセットをしても、ロックアウトはリセットされず、ロック時間を待機する必要がある

      [Amazon Cognito ユーザープール のメトリクス]
      https://docs.aws.amazon.com/ja_jp/cognito/latest/developerguide/metrics-for-cognito-user-pools.html
    */

      // cognito認証処理
      try {
        const user = await Auth.signIn({
          username: payload.username,
          password: payload.password,
        })
        commit('setUser', user)
      } catch (error) {
        commit('setUser', null) // cognito認証情報の無効化
        throw error
      }

      // サインインユーザーのバリデーション
      try {
        const body = { "id": payload.username }
        await API.post('/account/info/signinValidation', body)
      } catch (error) {
        // API呼出しで例外発生
        commit('setUser', null) // cognito認証情報の無効化
        throw error
      }
    },
    async doSignOut({ commit }) {
      await Auth.signOut()
      commit('reset')
    },
    async doSetFunction({ commit }, payload) {
      commit('setFunction', { functionCd: payload.functionCd, functionTitle: payload.functionTitle })
    },
    async doSetUserAttr({ commit }, payload) {
      commit('setUserAttr', payload)
    },
  },
  plugins: [createPersistedState({ key: 'sparq' })],
})

export default store